<template>
  <div>
    <b-container fluid class="geofence-edit p-0 m-0">
      <vue-resizable :min-height="50" :height="parseInt(theHeight)" class="w-100" :active="['b']" @resize:end="mapResized">
        <editable-map
          editable
          ref="map"
          id="map"
          :zoom.sync="currentZoom"
          :options="mapOptions"
          :center="center"
          :min-zoom="minZoom"
          :max-zoom="maxZoom"
          :bounds.sync="bounds"
          style="height: 100%; width: 100%"
          @ready="emitReady"
        >
          <l-tile-layer
            v-for="tileProvider in computedTileProviders"
            :key="tileProvider.name"
            :name="tileProvider.name"
            :visible="tileProvider.visible"
            :url="tileProvider.url"
            :attribution="tileProvider.attribution"
            layer-type="base"
            :options="tileProvider.options"
            v-if="tileProvidersReady"
          />
          <l-control-fullscreen position="topleft"
            :options="{title: { false: $gettext('Go fullscreen'), true: $gettext('Back to normal') }}"
          />
          <l-control-layers
            :position="layersPosition"
            :collapsed="false"
            :sort-layers="true"
          />
          <l-control-zoom :position="zoomPosition" />
          <l-control-attribution
            :position="attributionPosition"
            prefix="Moova"
          />
          <l-control-scale position="bottomright" :imperial="imperial" :metric="metric" />

          <l-control position="bottomleft">
            <div class="map-filters">
              <b-card class="mb-1 p-1">
                <div class="text-center">
                  <b-button size="sm" @click="fitMap" v-translate>
                    Fit map
                  </b-button>
                </div>
              </b-card>
              <b-card class="mb-0 p-1">
                <b-form-checkbox v-model="editableLayersSwitch"><translate>Current Geofence</translate></b-form-checkbox>
                <b-form-checkbox v-model="nearbyLayersSwitch">
                  <translate>Show Nearby Elements</translate>
                  <strong class="text-danger" v-if="nearbyLayersSwitch && currentZoom <= 12" :title="$gettext('Zoom-in to Search Again')" v-b-tooltip.hover>(!)</strong>
                </b-form-checkbox>
                <div class="ml-4" v-if="nearbyLayersSwitch">
                  <indeterminate-checkboxes all-selected :parent-title="$gettext('GeoFences') + ` (${notLinkedGeoFences.length})`" :parent-title-all-selected="$gettext('GeoFences') + ` (${notLinkedGeoFences.length})`" v-model="polygonTypes" :child-options="computedGeoTypesSelect.filter(x => x.type === 'polygon')" />
                  <indeterminate-checkboxes all-selected :parent-title="$gettext('GeoLines') + ` (${notLinkedGeoLines.length})`" :parent-title-all-selected="$gettext('GeoLines') + ` (${notLinkedGeoLines.length})`" v-model="lineTypes" :child-options="computedGeoTypesSelect.filter(x => x.type === 'linestring')" />
                  <indeterminate-checkboxes all-selected :parent-title="$gettext('GeoPoints') + ` (${notLinkedGeoPoints.length})`" :parent-title-all-selected="$gettext('GeoPoints') + ` (${notLinkedGeoPoints.length})`" v-model="pointTypes" :child-options="computedGeoTypesSelect.filter(x => x.type === 'point')" />
                </div>
              </b-card>
            </div>
          </l-control>

          <l-feature-group ref="nearbyLayers" id="nearbyLayers" :visible="nearbyLayersSwitch">
            <editable-polygon
              v-for="(geofence, index) in notLinkedGeoFences"
              :key="'notLinkedGeofence' + index"
              v-if="notLinkedGeoFences.length > 0 && geofence.coordinates"
              :lat-lngs="geofence.coordinates"
              :editable="false"
              :visible="polygonTypes.length > 0"
              :color="stringToColour('11' + geofence.geo_type.name)"
              :fillColor="stringToColour('11' + geofence.geo_type.name)"
              :weight="1"
            >
              <l-popup :options="{closeButton: false}">
                <b-button @click="addGeoFence($event, geofence)" variant="success" :title="$gettext('Add Relation to the Current GeoFence')" v-b-tooltip.hover><font-awesome-icon icon="link" /></b-button>
              </l-popup>
              <l-tooltip>
                <geofence-title :geofence="geofence" />
              </l-tooltip>
            </editable-polygon>

            <editable-polyline
              v-for="(line, index) in notLinkedGeoLines"
              :key="'notLinkedLine' + index"
              :lat-lngs="line.coordinates"
              :editable="false"
              :visible="lineTypes.length > 0"
              :color="line.geo_type.name.indexOf('entry') !== -1 ? '#006600' : (line.geo_type.name.indexOf('exit') !== -1 ? '#660000' : '#000')"
              :weight="1"
            >
              <l-popup :options="{closeButton: false}">
                <b-button @click="addGeoLine($event, line)" variant="success" :title="$gettext('Add Relation to the Current GeoFence')" v-b-tooltip.hover><font-awesome-icon icon="link" /></b-button>
              </l-popup>
              <l-tooltip>
                {{line.name}}
              </l-tooltip>
            </editable-polyline>

            <l-marker :lat-lng="point" v-for="(point, index) in notLinkedGeoPoints" :key="'notLinkedPoint' + index" :draggable="false" :visible="pointTypes.length > 0">
              <l-icon
                :icon-anchor="[9, 24]"
                style="position: relative;"
                :tooltipAnchor="[9, -12]"
                :icon-size="[18, 24]"
                >
              <font-awesome-icon icon="map-marker" size="2x" style="filter: drop-shadow(0 0 1px rgba(255,255,255,1)) drop-shadow(0 2px 2px rgba(0,0,0,.3));" :color="point.geo_type.name.indexOf('entry') !== -1 ? '#006600' : (point.geo_type.name.indexOf('exit') !== -1 ? '#660000' : '#000')" />
              </l-icon>
              <l-popup :options="{closeButton: false}">
                <b-button @click="addGeoPoint($event, point)" variant="success" :title="$gettext('Add Relation to the Current GeoFence')" v-b-tooltip.hover><font-awesome-icon icon="link" /></b-button>
              </l-popup>
              <l-tooltip>
                {{point.name}}
              </l-tooltip>
            </l-marker>
          </l-feature-group>

          <l-feature-group ref="editableLayers" id="editableLayers" :visible="editableLayersSwitch">
            <editable-polygon
              ref="geofence"
              v-if="geofence.coordinates"
              :editable="geofence.extraData.editable"
              :visible="geofence.extraData.visible"
              :lat-lngs="geofence.coordinates"
              :color="geofence.extraData.editable ? 'red' : '#3388ff'"
              :fillColor="geofence.extraData.editable ? 'red' : '#3388ff'"
              :weight="parseInt(geofence.extraData.weight)"
              @editable:editing="updateFenceEdited"
            >
              <l-tooltip>
                {{geofence.name}}
              </l-tooltip>
            </editable-polygon>

            <editable-polygon
              v-for="(geofence, index) in geoFences"
              :key="'geofence' + index"
              v-if="geoFences.length > 0 && geofence.coordinates"
              :editable="geofence.extraData.editable"
              :visible="geofence.extraData.visible"
              :lat-lngs="geofence.coordinates"
              :color="geofence.extraData.editable ? '#' + geoFencesColors[index] : '#3388ff'"
              :fillColor="geofence.extraData.editable ?  '#' + geoFencesColors[index] : '#3388ff'"
              :weight="parseInt(geofence.extraData.weight)"
              @editable:editing="updateFenceEdited($event, index)"
            >
              <l-tooltip>
                {{geofence.name}}
              </l-tooltip>
            </editable-polygon>

            <l-marker :lat-lng="point" v-for="(point, index) in geoPoints" :key="'point' + index" :draggable="point.extraData.editable" @update:latLng="updatePoint($event, index)" :visible="point.extraData.visible">
              <l-icon
                :icon-anchor="[9, 24]"
                style="position: relative;"
                :tooltipAnchor="[9, -12]"
                :icon-size="[18, 24]"
                >
              <font-awesome-icon icon="map-marker" size="2x" :transform="'grow-' + point.extraData.grow" style="filter: drop-shadow(0 0 1px rgba(255,255,255,1)) drop-shadow(0 2px 2px rgba(0,0,0,.3));" :color="point.geo_type.name.indexOf('entry') !== -1 ? '#00cc00' : (point.geo_type.name.indexOf('exit') !== -1 ? '#cc0000' : '#000')" />
              </l-icon>
              <l-tooltip>
                {{point.name}}
              </l-tooltip>
            </l-marker>

            <editable-polyline
              v-for="(line, index) in geoLines"
              :key="'line' + index"
              v-if="line.extraData"
              :lat-lngs="line.coordinates"
              :editable="line.extraData.editable"
              :visible="line.extraData.visible"
              :color="line.geo_type.name.indexOf('entry') !== -1 ? '#00cc00' : (line.geo_type.name.indexOf('exit') !== -1 ? '#cc0000' : '#000')"
              :weight="parseInt(line.extraData.weight)"
              @editable:editing="updateLine($event, index)"
            >
              <l-tooltip>
                {{line.name}}
              </l-tooltip>
            </editable-polyline>

          </l-feature-group>
        </editable-map>
      </vue-resizable>
    </b-container>
    <b-container fluid>
      <b-row class="my-3">
        <b-col>
          <h3>
            <translate>Main GeoFence</translate>
            <b-button size="sm" variant="danger" @click="geofence.extraData.editable = false; geofence.coordinates = []" v-if="geofence.coordinates && geofence.coordinates.length > 0">
              <font-awesome-icon icon="trash-alt" />
              <translate>Polygon</translate>
            </b-button>
            <b-button size="sm" variant="success" @click="addGeoFence($event, null, true)" v-else>
              <font-awesome-icon icon="plus-circle" />
              <translate>Polygon</translate>
            </b-button>
            <b-button size="sm" variant="dark" v-b-modal.modalFetchExternalData class="p-1">
              <font-awesome-icon :icon="['far', 'eye']" />
              <translate>External</translate>
            </b-button>
            <b-modal ref="modalFetchExternalData" id="modalFetchExternalData" hide-header hide-footer @hide="externalData = {}">
              <b-form @submit.prevent.stop="fetchExternalData">
                <b-form-group>
                  <label for="geofence-external-id" v-translate>Location External ID (exact)</label>
                  <b-form-input type="text" id="geofence-external-id" v-model="externalDataId" />
                </b-form-group>
                <b-form-group>
                  <b-button size="sm" type="submit" v-translate>Fetch Data</b-button>
                </b-form-group>

                <b-alert variant="warning" :show="!!externalErrorMessage" class="mt-3">
                  <h4 class="mb-0" v-html="externalErrorMessage"></h4>
                </b-alert>

                <div v-if="Object.keys(this.externalData).length > 0">
                  <div v-if="geofence.external_id">
                    <b-table :items="externalDataItems" :fields="['field', 'local', 'remote']"></b-table>
                    <hr>
                    <b-button-group>
                      <b-button size="sm" variant="success" @click="overwriteWithExternalData" class="p-1">
                        <translate>Overwrite Current Data (No Shape)</translate>
                      </b-button>
                      <b-button size="sm" variant="warning" @click="importExternalData" class="p-1">
                        <translate>Overwrite and Re-Create Shape</translate>
                      </b-button>
                      <b-button size="sm" variant="danger" @click="$refs.modalFetchExternalData.hide()" class="p-1">
                        <translate>Close and Dismiss</translate>
                      </b-button>
                    </b-button-group>
                  </div>
                  <div v-else>
                    <b-table :items="externalDataItems" :fields="['field', 'remote']"></b-table>
                    <hr>
                    <b-button-group>
                      <b-button size="sm" variant="success" @click="importExternalData" class="p-1">
                        <translate>Import External Data and Create Basic Geofence Shape</translate>
                      </b-button>
                      <b-button size="sm" variant="danger" @click="$refs.modalFetchExternalData.hide()" class="p-1">
                        <translate>Close and Dismiss</translate>
                      </b-button>
                    </b-button-group>
                  </div>
                </div>
              </b-form>
            </b-modal>
          </h3>
          <geofence-form ref="mainGeofence" :geofence="geofence" @updated="getGeofence" v-if="geofence.coordinates" @center="centerElement" />
        </b-col>
        <b-col>
          <div v-if="isEdit">
            <h4>
              <translate>GeoFences</translate>
              <b-button size="sm" @click="addGeoFence">
                <font-awesome-icon icon="plus-circle" />
                <translate>GeoFence</translate>
              </b-button>
              <b-button size="sm" :variant="!acGeofence ? 'primary' : 'dark'" @click="acGeofence = !acGeofence" :title="acGeofence ? $gettext('Close Autocomplete') : $gettext('Search for Existing GeoFences')" v-b-tooltip.hover>
                <font-awesome-icon icon="search" />
              </b-button>
            </h4>
            <b-card class="mb-3 bg-dark" v-if="acGeofence">
              <geofence-autocomplete :restrict-to-types="['yard', 'loading_zone']" @selected="addGeoFence($event, $event)" />
            </b-card>

            <b-card v-for="(geofence, index) in geoFences" :key="'geofenceData' + index" v-if="geoFences.length > 0 && geofence.coordinates" :style="'border-top: 5px solid #' + geoFencesColors[index] + ' !important;'" class="mb-2">
              <geofence-form :geofence="geofence" @updated="getGeofence" @remove="geoFences.splice(index, 1)" :index="index" @center="centerElement" />
            </b-card>
          </div>
        </b-col>
        <b-col>
          <div v-if="isEdit">
            <h4>
              <translate>GeoLines</translate>
              <b-button size="sm" @click="addGeoLine">
                <font-awesome-icon icon="plus-circle" />
                <translate>GeoLine</translate>
              </b-button>
              <b-button size="sm" :variant="!acLine ? 'primary' : 'dark'" @click="acLine = !acLine" :title="acLine ? $gettext('Close Autocomplete') : $gettext('Search for Existing GeoLines')" v-b-tooltip.hover>
                <font-awesome-icon icon="search" />
              </b-button>
            </h4>
            <b-card class="mb-3 bg-dark" v-if="acLine">
              <geoline-autocomplete @selected="addGeoLine($event, $event)" />
            </b-card>
            <b-card v-for="(geoLine, index) in geoLines" :key="'geoLineData' + index" v-if="geoLines.length > 0 && geoLine.coordinates" :style="'border-top: 5px solid ' + (geoLine.geo_type.name.indexOf('entry') !== -1 ? '#00cc00' : (geoLine.geo_type.name.indexOf('exit') !== -1 ? '#cc0000' : '#000')) + ' !important;'">
              <geo-line-form :geo-line="geoLine" @updated="getGeofence" @remove="geoLines.splice(index, 1)" :index="index" @center="centerElement" />
            </b-card>
          </div>
        </b-col>
        <b-col>
          <div v-if="isEdit">
            <h4>
              <translate>GeoPoints</translate>
              <b-button size="sm" @click="addGeoPoint">
                <font-awesome-icon icon="plus-circle" />
                <translate>GeoPoint</translate>
              </b-button>
              <b-button size="sm" :variant="!acPoint ? 'primary' : 'dark'" @click="acPoint = !acPoint" :title="acPoint ? $gettext('Close Autocomplete') : $gettext('Search for Existing GeoPoints')" v-b-tooltip.hover>
                <font-awesome-icon icon="search" />
              </b-button>
            </h4>
            <b-card class="mb-3 bg-dark" v-if="acPoint">
              <geopoint-autocomplete @selected="addGeoPoint($event, $event)" />
            </b-card>
            <b-card v-for="(geoPoint, index) in geoPoints" :key="'geoPointData' + index" v-if="geoPoints.length > 0 && geoPoint.lat && geoPoint.lng" :style="'border-top: 5px solid ' + (geoPoint.geo_type.name.indexOf('entry') !== -1 ? '#00cc00' : (geoPoint.geo_type.name.indexOf('exit') !== -1 ? '#cc0000' : '#000')) + ' !important;'">
              <geo-point-form :geo-point="geoPoint" @updated="getGeofence" @remove="geoPoints.splice(index, 1)" :index="index" @center="centerElement" />
            </b-card>
          </div>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import {
  GeofenceService,
  GeoLineService,
  GeoPointService
} from '@/services/api'
import IndeterminateCheckboxes from '@/components/IndeterminateCheckboxes'
import GeofenceForm from '@/components/Geofences/GeofenceForm'
import GeoLineForm from '@/components/Geofences/GeoLineForm'
import GeoPointForm from '@/components/Geofences/GeoPointForm'
import GeofenceAutocomplete from '@/components/Autocompletes/GeofenceAutocomplete'
import GeolineAutocomplete from '@/components/Autocompletes/GeolineAutocomplete'
import GeopointAutocomplete from '@/components/Autocompletes/GeopointAutocomplete'
import VueResizable from 'vue-resizable'
import L from 'leaflet'
import { EditableMap, EditablePolygon, EditablePolyline } from 'vue2-leaflet-editable'
import {
  LTileLayer,
  LFeatureGroup,
  LMarker,
  LTooltip,
  LPopup,
  LControlZoom,
  LControlAttribution,
  LControlScale,
  LControlLayers,
  LControl,
  LIcon
} from 'vue2-leaflet'
import LControlFullscreen from 'vue2-leaflet-fullscreen'
import { SearchControl, EsriProvider } from 'leaflet-geosearch'
import CoordinatesProvider from '@/components/Maps/LeafletGeosearchProviders/CoordinatesProvider'

export default {
  name: 'GeofenceEdit',
  components: {
    IndeterminateCheckboxes,
    GeofenceForm,
    GeoLineForm,
    GeoPointForm,
    GeofenceAutocomplete,
    GeolineAutocomplete,
    GeopointAutocomplete,
    VueResizable,
    EditableMap,
    EditablePolygon,
    EditablePolyline,
    LTileLayer,
    LFeatureGroup,
    LMarker,
    LTooltip,
    LPopup,
    LControlZoom,
    LControlAttribution,
    LControlScale,
    LControlLayers,
    LControl,
    LIcon,
    LControlFullscreen
  },
  data() {
    return {
      geoTypesSelect: [],
      hash: null,
      updating: false,
      height: 500,
      theHeight: 0,
      geofenceModel: {
        geo_type_id: null,
        name: '',
        reference_location: '',
        external_id: '',
        address: null,
        city: null,
        zip: null,
        state: null,
        country: null,
        lat: null,
        lng: null,
        fence: [],
        timezone: null,
        editable: true
      },
      geofence: {},
      geoFences: [],
      geoPoints: [],
      geoLines: [],
      geoFencesColors: [
        '8A89E1',
        'C5E662',
        '4DA2FF',
        'FFB54D',
        'F03A47',
        '1B998B',
        '6d05d6',
        '000000',
        'C5D86D',
        '712F79',
        'F7996E',
        '48639C',
        'CA895F',
        '4E3822',
        '06BEE1',
        'ff6600',
        '319200',
        '653300',
        '884cff',
        'FF6C87'
      ],
      map: null,
      mapReady: false,
      center: [-34.6158036, -58.5035093],
      bounds: null,
      opacity: 1,
      mapOptions: {
        zoomControl: false,
        attributionControl: false,
        zoomSnap: true
      },
      currentZoom: 5,
      minZoom: 1,
      maxZoom: 18,
      zoomPosition: 'topleft',
      layersPosition: 'topright',
      attributionPosition: 'bottomright',
      imperial: false,
      metric: false,
      tileProvidersReady: false,
      tileProvidersLocal: [],
      editableLayers: null,
      editableLayersSwitch: true,
      nearbyLayersSwitch: false,
      nearbyElements: ['GeoFences', 'GeoLines', 'GeoPoints'],
      notLinkedGeoFences: [],
      notLinkedGeoLines: [],
      notLinkedGeoPoints: [],
      acGeofence: false,
      acLine: false,
      acPoint: false,
      polygonTypes: [],
      lineTypes: [],
      pointTypes: [],
      searching: {
        geoFences: false,
        geoLines: false,
        geoPoints: false
      },
      externalDataId: 'RC3.',
      externalData: {},
      externalDataMap: {
        name: 'LOCATION_NAME',
        external_id: 'LOCATION_GID',
        address: 'ADDRESS_LINE',
        city: 'CITY',
        zip: 'POSTAL_CODE',
        state: 'STATE_CODE',
        lat: 'LAT',
        lng: 'LON',
        timezone: 'TIME_ZONE'
      },
      externalDataItems: [],
      externalErrorMessage: ''
    }
  },
  computed: {
    geofenceHasChanges() {
      return JSON.stringify(this.geofence) !== this.hash
    },
    computedTileProviders() {
      return this.setDefaultTileProvider()
    },
    computedGeoTypesSelect() {
      return this.geoTypes.map(x => {
        return {
          text: this.toTitleCase(x.name),
          value: x.id,
          type: x.type
        }
      })
    },
    isEdit() {
      return !!this.$route.params.id
    }
  },
  watch: {
    tileProviders: {
      handler(newVal, oldVal) {
        this.tileProvidersLocal = JSON.parse(JSON.stringify(newVal))
        // console.log(this.tileProvidersLocal)
        this.setDefaultTileProvider()
      },
      deep: true
    },
    nearbyLayersSwitch(val) {
      if (val) {
        this.getNearbyElements()
      }
    },
    bounds: {
      handler(newVal, oldVal) {
        this.map.closePopup()
        this.getNearbyElements()
      },
      deep: true
    },
    polygonTypes(val) {
      this.getNearbyElements()
    },
    lineTypes(val) {
      this.getNearbyElements()
    },
    pointTypes(val) {
      this.getNearbyElements()
    }
  },
  async mounted() {
    this.imperial = this.frontConfig?.i18n?.unitSystem === 'imperial'
    this.metric = !this.imperial
    this.theHeight = this.$cookies.get('geofence-map-height') || this.height
    this.geoTypesSelect = this.geoTypes.map(x => {
      return {
        text: this.toTitleCase(x.name),
        value: x.id,
        type: x.type
      }
    })
    if (this.isEdit) {
      this.getGeofence()
    } else {
      this.geofence = {...this.geofenceModel}
      this.geofence.lat = 38.129347
      this.geofence.lng = -121.208467
      this.geofence.coordinates = []
      this.geofence.extraData = {
        editable: true,
        visible: true
      }
      this.zoom = 12
    }

    await this.checkMapProviders()
    this.center = this.frontConfig && this.frontConfig.maps && this.frontConfig.maps.general && this.frontConfig.maps.general.lat ? [this.frontConfig.maps.general.lat, this.frontConfig.maps.general.lng] : [0, 0]
    if (this.geolocate === true) {
      this.geolocateMap()
    }
    this.currentZoom = this.zoom
    this.tileProvidersLocal = JSON.parse(JSON.stringify(this.tileProviders))
  },
  methods: {
    async fetchExternalData() {
      let locationExists = false
      this.externalData = {}
      this.externalErrorMessage = ''
      if (!this.geofence.external_id) {
        const params = {
          filter_groups: [{
            filters: [
              ['external_id', 'eq', this.externalDataId]
            ]
          }]
        }
        locationExists = await GeofenceService.getAll({params})
          .then(response => {
            return response.body.data.length > 0 ? response.body.data[0] : false
          })
      }

      if (this.geofence.external_id || !locationExists) {
        GeofenceService.fetchExternalData({params: {id: this.externalDataId}})
          .then(response => {
            console.log(response.body.data[0])
            if (response.body.data.length > 0) {
              this.externalData = response.body.data[0]
              if (this.geofence.external_id) {
                let externalDataItems = []
                for (let i = 0; i < Object.keys(this.externalDataMap).length; i++) {
                  const localKey = Object.keys(this.externalDataMap)[i]
                  const externalKey = Object.values(this.externalDataMap)[i]
                  externalDataItems.push({
                    field: this.toTitleCase(localKey),
                    local: this.geofence[localKey],
                    remote: this.externalData[externalKey]
                  })
                }
                this.externalDataItems = externalDataItems
              } else {
                let externalDataItems = []
                for (let i = 0; i < Object.keys(this.externalDataMap).length; i++) {
                  const localKey = Object.keys(this.externalDataMap)[i]
                  const externalKey = Object.values(this.externalDataMap)[i]
                  externalDataItems.push({
                    field: this.toTitleCase(localKey),
                    remote: this.externalData[externalKey]
                  })
                }
                this.externalDataItems = externalDataItems
              }
            } else {
              this.externalErrorMessage = this.$gettext('There are no results for this search. Please check the External ID.')
            }
          })
      } else {
        this.externalErrorMessage = `${this.$gettext('The Location you are trying to import ALREADY EXISTS.')}`
        this.externalErrorMessage += `<br><small><a href="${this.$router.resolve({name: 'GeofencesEdit', params: {'id': locationExists.id}}).href}" target="_blank">${this.$gettext('Click to Edit Existing')}</a></small>`
      }
    },
    overwriteWithExternalData() {
      for (let i = 0; i < Object.keys(this.externalDataMap).length; i++) {
        const localKey = Object.keys(this.externalDataMap)[i]
        const externalKey = Object.values(this.externalDataMap)[i]
        this.geofence[localKey] = this.externalData[externalKey]
      }
      this.$refs.mainGeofence.updateGeofence()
      this.$refs.modalFetchExternalData.hide()
    },
    importExternalData() {
      for (let i = 0; i < Object.keys(this.externalDataMap).length; i++) {
        const localKey = Object.keys(this.externalDataMap)[i]
        const externalKey = Object.values(this.externalDataMap)[i]
        this.geofence[localKey] = this.externalData[externalKey]
      }
      this.currentZoom = 18
      setTimeout(() => {
        this.center = [this.geofence.lat, this.geofence.lng]
        setTimeout(() => {
          this.addGeoFence(null, null, true)
          this.$refs.modalFetchExternalData.hide()
        }, 1000)
      }, 1000)
    },
    setFenceCoordinates(geofence) {
      geofence.parent = this.$route.params.id
      // console.log('setFenceCoordinates > ', geofence)

      // Backup
      geofence.originalFence = !geofence.originalFence ? Object.assign({}, geofence.fence) : geofence.originalFence

      // For map
      let cleanupDupes = geofence.fence && geofence.fence.coordinates ? [Array.from(new Set(geofence.fence.coordinates[0].map(a => a.join('|'))), s => s.split('|').map(Number))] : null
      geofence.coordinates = cleanupDupes ? cleanupDupes.map(x =>
        x.map(([lng, lat]) => ({lat, lng}))
      ) : geofence.coordinates

      // To save
      geofence.fence = cleanupDupes ? cleanupDupes.map(x => {
        return x.map(y => [y[1], y[0]])
      })[0] : geofence.fence
      return geofence
    },
    setLineCoordinates(line) {
      line.parent = this.$route.params.id
      // console.log('setLineCoordinates > ', line)

      // Backup
      line.originalPath = !line.originalPath ? Object.assign({}, line.path) : line.originalPath

      // For map
      let cleanupDupes = line.path && line.path.coordinates ? Array.from(new Set(line.path.coordinates.map(a => a.join('|'))), s => s.split('|').map(Number)) : null
      line.coordinates = cleanupDupes ? cleanupDupes.map(([lng, lat]) => ({lat, lng})) : line.coordinates

      // To save
      line.path = cleanupDupes ? cleanupDupes.map(x => [x[1], x[0]]) : line.path
      return line
    },
    setPointCoordinates(point) {
      point.parent = this.$route.params.id

      return point
    },
    async getGeofence() {
      const params = {
        params: {
          includes: [
            'geo_type',
            'geo_fences.geo_type',
            'geo_lines.geo_type',
            'geo_points.geo_type'
          ]
        }
      }
      return GeofenceService.get(this.$route.params.id, params)
        .then(response => {
          // Main GeoFence
          this.geofence = {...this.setFenceCoordinates(response.body), extraData: {visible: true, weight: 2, editable: true}}

          // Related GeoFences
          this.geoFences = []
          this.geofence.geo_fences.map(x => this.setFenceCoordinates(x)).forEach(fence => {
            this.geoFences.push({...fence, extraData: {visible: true, weight: 2, editable: true}})
          })
          this.geofence.geo_fences = this.geoFences

          // Related GeoLines
          this.geoLines = []
          this.geofence.geo_lines.map(x => this.setLineCoordinates(x)).forEach(line => {
            this.geoLines.push({...line, extraData: {visible: true, weight: 2, editable: true}})
          })
          this.geofence.geo_lines = this.geoLines

          // Related GeoPoints
          this.geoPoints = []
          this.geofence.geo_points.map(x => this.setPointCoordinates(x)).forEach(point => {
            this.geoPoints.push({...point, extraData: {visible: true, grow: 0, editable: true}})
          })
          this.geofence.geo_points = this.geoPoints

          this.externalDataId = this.geofence.external_id || null

          // Reset de hash
          this.$nextTick(() => {
            let geofenceHash = Object.assign({}, this.geofence)
            delete geofenceHash.extraData
            this.hash = JSON.stringify(Object.assign({}, geofenceHash))
            this.resetEditables()
            this.fitMap()
          })
        })
    },
    updateGeofence() {
      GeofenceService.update(this.$route.params.id, this.geofence)
        .then(response => {
          this.$nextTick(() => {
            this.getGeofence()
          })
        })
    },
    resetEditables() {
      this.editableLayersSwitch = false
      this.$nextTick(() => {
        this.editableLayersSwitch = true
      })
    },
    getNearbyElements() {
      if (this.nearbyLayersSwitch && this.map.getZoom() > 12) {
        if (this.polygonTypes.length > 0) {
          if (!this.searching.geoFences) {
            this.searching.geoFences = true
            let params = {
              includes: [
                'geo_type'
              ],
              filter_groups: [
                {
                  filters: [
                    ['geo_type_id', 'in', this.polygonTypes],
                    ['boundary', 'eq', this.bounds._southWest.lat + ',' + this.bounds._southWest.lng + '|' + this.bounds._northEast.lat + ',' + this.bounds._northEast.lng]
                  ]
                }
              ]
            }
            GeofenceService.getAll({params})
              .then(response => {
                this.notLinkedGeoFences = response.body.data.filter(x => this.geoFences.map(y => y.id).indexOf(x.id) === -1 && x.id !== this.geofence.id).map(x => this.setFenceCoordinates(x))
                this.searching.geoFences = false
              })
          }
        } else {
          this.notLinkedGeoFences = []
          this.searching.geoFences = false
        }
        if (this.lineTypes.length > 0) {
          if (!this.searching.geoLines) {
            this.searching.geoLines = true
            let params = {
              includes: [
                'geo_type'
              ],
              filter_groups: [
                {
                  filters: [
                    ['geo_type_id', 'in', this.lineTypes],
                    ['boundary', 'eq', this.bounds._southWest.lat + ',' + this.bounds._southWest.lng + '|' + this.bounds._northEast.lat + ',' + this.bounds._northEast.lng]
                  ]
                }
              ]
            }
            GeoLineService.getAll({params})
              .then(response => {
                this.notLinkedGeoLines = response.body.data.filter(x => this.geoLines.map(y => y.id).indexOf(x.id) === -1).map(x => this.setLineCoordinates(x))
                this.searching.geoLines = false
              })
          }
        } else {
          this.notLinkedGeoLines = []
          this.searching.geoLines = false
        }
        if (this.pointTypes.length > 0) {
          if (!this.searching.geoPoints) {
            this.searching.geoPoints = true
            let params = {
              includes: [
                'geo_type'
              ],
              filter_groups: [
                {
                  filters: [
                    ['geo_type_id', 'in', this.pointTypes],
                    ['boundary', 'eq', this.bounds._southWest.lat + ',' + this.bounds._southWest.lng + '|' + this.bounds._northEast.lat + ',' + this.bounds._northEast.lng]
                  ]
                }
              ]
            }
            GeoPointService.getAll({params})
              .then(response => {
                this.notLinkedGeoPoints = response.body.data.filter(x => this.geoPoints.map(y => y.id).indexOf(x.id) === -1).map(x => this.setPointCoordinates(x))
                this.searching.geoPoints = false
              })
          }
        } else {
          this.notLinkedGeoPoints = []
          this.searching.geoPoints = false
        }
      }
    },
    setDefaultTileProvider() {
      if (this.ecosystemMaps && this.ecosystemMaps !== null && this.ecosystemMaps.defaultTileProvider && this.ecosystemMaps.defaultTileProvider[this.$options.name]) {
        let tileProvidersLocal = this.tileProvidersLocal.map(x => {
          if (x.name === this.ecosystemMaps.defaultTileProvider[this.$options.name]) {
            x.visible = true
          } else {
            x.visible = false
          }
          return x
        })
        this.$nextTick(() => {
          this.tileProvidersReady = true
        })
        return tileProvidersLocal
      }
      this.tileProvidersReady = true
    },
    fitMap() {
      this.$nextTick(() => {
        // just in case
        this.editableLayers = this.$refs.editableLayers.mapObject
        // fit bounds
        this.map.fitBounds(this.editableLayers.getBounds())
        this.map.invalidateSize()
      })
    },
    async emitReady() {
      this.map = this.$refs.map.mapObject
      this.map.invalidateSize()
      if (!this.mapReady) {
        // geosearch control
        const searchControl = new SearchControl({
          style: 'button',
          provider: new EsriProvider({params: {sourceCountry: this.profile.country_iso_code}}),
          position: 'topright',
          searchLabel: this.$gettext('Search City'),
          showMarker: false,
          autoCompleteDelay: 250
        })
        this.map.addControl(searchControl)
        const searchControl2 = new SearchControl({
          style: 'button',
          provider: new CoordinatesProvider({params: {sourceCountry: this.profile.country_iso_code}}),
          position: 'topleft',
          searchLabel: this.$gettext('Go to Coordinates'),
          showMarker: false,
          autoCompleteDelay: 0,
          resultFormat: ({ result }) => result.visibleLabel
        })
        this.map.addControl(searchControl2)
      }
      this.mapReady = true
      this.$emit('ready', true)
      this.setDefaultTileProvider()
      this.editableLayers = this.$refs.editableLayers.mapObject
      this.center = [this.geofence.lat, this.geofence.lng]
    },
    updateFenceEdited($event, index = null) {
      if (index === null) {
        this.geofence.editable = false
        this.geofence.fence = []
        this.geofence.fence = $event.layer.getLatLngs()[0].map(x => {
          return [x.lat, x.lng]
        })
        this.geofence.editable = true
      } else {
        this.geoFences[index].editable = false
        this.geoFences[index].fence = []
        this.geoFences[index].fence = $event.layer.getLatLngs()[0].map(x => {
          return [x.lat, x.lng]
        })
        this.geoFences[index].editable = true
      }
    },
    updateLine($event, index = null) {
      if (index === null) {
        this.geofence.editable = false
        this.geofence.fence = []
        this.geofence.fence = $event.layer.getLatLngs().map(x => {
          return [x.lat, x.lng]
        })
        this.geofence.editable = true
      } else {
        this.geoLines[index].editable = false
        this.geoLines[index].path = []
        this.geoLines[index].path = $event.layer.getLatLngs().map(x => {
          return [x.lat, x.lng]
        })
        this.geoLines[index].editable = true
      }
    },
    updatePoint($event, index) {
      this.geoPoints[index] = Object.assign(this.geoPoints[index], $event)
    },
    addGeoFence($event, geofence = null, principal = false) {
      if (!geofence) {
        let bounds = this.map.getBounds()
        let northEast = bounds.getNorthEast()
        let southWest = bounds.getSouthWest()
        let center = bounds.getCenter()
        let degree = 1
        let f = Math.pow(0.66, degree)

        let path = {
          type: 'Polygon',
          coordinates: [
            [
              [center.lng, (1 - f) * center.lat + (f) * northEast.lat],
              [(1 - f) * center.lng + (f) * southWest.lng, (1 - f) * center.lat + (f) * southWest.lat],
              [(1 - f) * center.lng + (f) * northEast.lng, (1 - f) * center.lat + (f) * southWest.lat]
            ]
          ]
        }
        if (!principal) {
          geofence = {...this.geofenceModel, fence: path}
        } else {
          geofence = {...this.geofence, fence: path}
        }
      } else {
        geofence = {...geofence, adding: true}
      }
      geofence = {...geofence, extraData: {visible: true, weight: 2, editable: true}}
      if (!principal) {
        this.geofence.geo_fences.push(this.setFenceCoordinates(geofence))
        this.geoFences = this.geofence.geo_fences
        this.fitMap()
      } else {
        this.geofence = {}
        this.$nextTick(() => {
          this.geofence = this.setFenceCoordinates(geofence)
          this.fitMap()
        })
      }
    },
    addGeoLine($event, geoline = null) {
      if (!geoline) {
        let bounds = this.map.getBounds()
        let northEast = bounds.getNorthEast()
        let southWest = bounds.getSouthWest()
        let center = bounds.getCenter()
        let degree = 1
        let f = Math.pow(0.66, degree)

        let path = {
          type: 'LineString',
          coordinates: [
            [(1 - f) * center.lng + (f) * southWest.lng, (1 - f) * center.lat + (f) * southWest.lat],
            [(1 - f) * center.lng + (f) * northEast.lng, (1 - f) * center.lat + (f) * southWest.lat]
          ]
        }

        geoline = {
          geo_type_id: null,
          name: '',
          path: path,
          editable: true,
          geo_type: {
            name: ''
          }
        }
      } else {
        geoline = {...geoline, adding: true}
      }
      geoline = {...geoline, extraData: {visible: true, weight: 2, editable: true}}

      this.geofence.geo_lines.push(this.setLineCoordinates(geoline))
      this.geoLines = this.geofence.geo_lines
      this.fitMap()
    },
    addGeoPoint($event, geopoint = null) {
      if (!geopoint) {
        let bounds = this.map.getBounds()
        let center = bounds.getCenter()
        let point = {
          type: 'Point',
          coordinates: [center.lng, center.lat]
        }
        geopoint = {
          geo_type_id: null,
          name: '',
          point: point,
          editable: true,
          geo_type: {
            name: ''
          },
          lat: center.lat,
          lng: center.lng
        }
      } else {
        geopoint = {...geopoint, adding: true}
      }

      geopoint = {...geopoint, extraData: {visible: true, grow: 0, editable: true}}

      this.geofence.geo_points.push(this.setPointCoordinates(geopoint))
      this.geoPoints = this.geofence.geo_points
      this.fitMap()
    },
    centerElement(type, coordinates) {
      let element
      if (type === 'fence') {
        element = L.polygon(coordinates)
        this.map.fitBounds(element.getBounds())
      } else if (type === 'line') {
        element = L.polyline(coordinates)
        this.map.fitBounds(element.getBounds())
      } else {
        this.center = coordinates
      }
    },
    mapResized(e) {
      if (e) {
        this.$cookies.set('geofence-map-height', e.height)
      }
      this.emitReady()
        .then(() => this.fitMap())
    }
  }
}
</script>

<style lang="scss">
.leaflet-control-geosearch form input {
  height: 30px !important;
}
.leaflet-touch .leaflet-bar a {
  &.reset {
    border-bottom: 0 !important;
  }
  &:last-child {
    border-radius: 4px !important;
    &.leaflet-control-zoom-out {
      border-radius: 0 0 2px 2px !important;
    }
  }
}
</style>
